import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../../hooks/useAuth'
import { supabase, db, callEdgeFunction } from '../../lib/supabase'
import { ErrorAlert } from '../../components/ui'
import { SALUTATIONS, PROFESSIONAL_ROLES } from '../../lib/constants'

const inputClass = "w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"

export default function FacilitatorProfile() {
  const { t, i18n } = useTranslation('admin')
  const { user, profile, refreshProfile } = useAuth()

  // Profile form state
  const [formData, setFormData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Email change state
  const [newEmail, setNewEmail] = useState('')
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailError, setEmailError] = useState(null)
  const [emailSent, setEmailSent] = useState(false)

  useEffect(() => {
    if (profile) {
      setFormData({
        salutation: profile.salutation || '',
        full_name: profile.full_name || '',
        organization_name: profile.organization_name || '',
        organization_address: profile.organization_address || '',
        professional_role: profile.professional_role || '',
        professional_role_other: profile.professional_role_other || '',
      })
    }
  }, [profile])

  const updateField = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    if (!formData.full_name.trim()) {
      setError(t('facilitatorProfile.validation.fullNameRequired'))
      return
    }

    if (!formData.organization_name.trim()) {
      setError(t('facilitatorProfile.validation.organisationRequired'))
      return
    }

    if (!formData.organization_address.trim()) {
      setError(t('facilitatorProfile.validation.addressRequired'))
      return
    }

    if (!formData.professional_role) {
      setError(t('facilitatorProfile.validation.selectRole'))
      return
    }

    if (formData.professional_role === 'Others' && !formData.professional_role_other.trim()) {
      setError(t('facilitatorProfile.validation.specifyRole'))
      return
    }

    setLoading(true)
    try {
      const updates = { ...formData }
      if (updates.professional_role !== 'Others') {
        updates.professional_role_other = ''
      }
      await db.updateProfile(user.id, updates)
      await refreshProfile()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      // Notify admin in background (fire-and-forget)
      callEdgeFunction('notify-admin-profile-change', { change_type: 'profile' }).catch(() => {})
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleEmailChange = async (e) => {
    e.preventDefault()
    setEmailError(null)
    setEmailSent(false)

    const trimmed = newEmail.trim().toLowerCase()
    if (!trimmed) {
      setEmailError(t('facilitatorProfile.changeEmail.enterEmail'))
      return
    }

    if (trimmed === user.email.toLowerCase()) {
      setEmailError(t('facilitatorProfile.changeEmail.sameEmail'))
      return
    }

    setEmailLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ email: trimmed })
      if (error) throw error
      await db.setPendingEmail(user.id, trimmed)
      await refreshProfile()
      setEmailSent(true)
      setNewEmail('')
      // Notify admin in background (fire-and-forget)
      callEdgeFunction('notify-admin-profile-change', { change_type: 'email', new_email: trimmed }).catch(() => {})
    } catch (err) {
      setEmailError(err.message)
    } finally {
      setEmailLoading(false)
    }
  }

  if (!formData) {
    return <div className="text-center py-12 text-gray-500">{t('facilitatorProfile.loadingProfile')}</div>
  }

  return (
    <div className="animate-fadeIn max-w-2xl mx-auto">
      <div className="mb-6">
        <Link to="/facilitator" className="text-gray-500 hover:text-gray-700 font-semibold transition-colors">
          {t('facilitatorProfile.backToDashboard')}
        </Link>
      </div>

      {/* Pending email verification banner */}
      {profile?.pending_email && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-800">{t('facilitatorProfile.pendingEmailBanner.title')}</p>
              <p className="text-sm text-amber-700 mt-1" dangerouslySetInnerHTML={{ __html: t('facilitatorProfile.pendingEmailBanner.message', { email: profile.pending_email }) }} />
            </div>
          </div>
        </div>
      )}

      {/* Profile form */}
      <div className="bg-white rounded-xl p-8 shadow-sm mb-6">
        <h2 className="font-display text-2xl font-medium text-gray-800 mb-6">{t('facilitatorProfile.title')}</h2>

        <ErrorAlert message={error} onDismiss={() => setError(null)} />

        {success && (
          <div className="mb-4 text-sm font-semibold text-green-600 bg-green-50 rounded-lg px-4 py-3">
            {t('facilitatorProfile.profileUpdated')}
          </div>
        )}

        <form onSubmit={handleProfileSubmit} className="space-y-5">
          <div className="grid grid-cols-[140px_1fr] gap-4">
            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorProfile.salutationLabel')}</label>
              <select
                className={inputClass}
                value={formData.salutation}
                onChange={updateField('salutation')}
              >
                <option value="">{t('facilitatorProfile.salutationPlaceholder')}</option>
                {SALUTATIONS.map((s, idx) => {
                  const labels = i18n.t('salutations', { ns: 'common', returnObjects: true })
                  return <option key={s} value={s}>{Array.isArray(labels) ? labels[idx] || s : s}</option>
                })}
              </select>
            </div>
            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorProfile.fullNameLabel')}</label>
              <input
                type="text"
                className={inputClass}
                value={formData.full_name}
                onChange={updateField('full_name')}
                required
              />
            </div>
          </div>

          <div>
            <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorProfile.organisationNameLabel')}</label>
            <input
              type="text"
              className={inputClass}
              value={formData.organization_name}
              onChange={updateField('organization_name')}
              required
            />
          </div>

          <div>
            <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorProfile.organisationAddressLabel')}</label>
            <textarea
              className={inputClass + ' resize-none'}
              rows={2}
              value={formData.organization_address}
              onChange={updateField('organization_address')}
              required
            />
          </div>

          <div>
            <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorProfile.professionalRoleLabel')}</label>
            <select
              className={inputClass}
              value={formData.professional_role}
              onChange={updateField('professional_role')}
              required
            >
              <option value="">{t('facilitatorProfile.professionalRolePlaceholder')}</option>
              {PROFESSIONAL_ROLES.map((role, idx) => {
                const labels = i18n.t('professionalRoles', { ns: 'common', returnObjects: true })
                return <option key={role} value={role}>{Array.isArray(labels) ? labels[idx] || role : role}</option>
              })}
            </select>
          </div>

          {formData.professional_role === 'Others' && (
            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorProfile.specifyRoleLabel')}</label>
              <input
                type="text"
                className={inputClass}
                value={formData.professional_role_other}
                onChange={updateField('professional_role_other')}
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? t('facilitatorProfile.saving') : t('facilitatorProfile.saveChanges')}
          </button>
        </form>
      </div>

      {/* Email change section */}
      <div className="bg-white rounded-xl p-8 shadow-sm">
        <h2 className="font-display text-2xl font-medium text-gray-800 mb-6">{t('facilitatorProfile.changeEmail.title')}</h2>

        <div className="mb-4">
          <span className="text-sm font-semibold text-gray-500">{t('facilitatorProfile.changeEmail.currentEmail')}</span>
          <p className="text-gray-800">{user?.email}</p>
        </div>

        <ErrorAlert message={emailError} onDismiss={() => setEmailError(null)} />

        {emailSent && (
          <div className="mb-4 text-sm font-semibold text-green-600 bg-green-50 rounded-lg px-4 py-3">
            {t('facilitatorProfile.changeEmail.verificationSent')}
          </div>
        )}

        <form onSubmit={handleEmailChange} className="space-y-4">
          <div>
            <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorProfile.changeEmail.newEmailLabel')}</label>
            <input
              type="email"
              className={inputClass}
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder={t('facilitatorProfile.changeEmail.newEmailPlaceholder')}
              required
            />
          </div>

          <p className="text-xs text-gray-500">
            {t('facilitatorProfile.changeEmail.verificationNote')}
          </p>

          <button
            type="submit"
            disabled={emailLoading}
            className="px-6 py-3 text-sm font-semibold text-primary-600 bg-primary-50 rounded-xl hover:bg-primary-100 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {emailLoading ? t('facilitatorProfile.changeEmail.sending') : t('facilitatorProfile.changeEmail.requestChange')}
          </button>
        </form>
      </div>
    </div>
  )
}
